/**
 * NikahElegant — content model + resolver.
 *
 * Data-driven template for an elegant Muslim Nikah ceremony.
 * Maps the generic InvitationData (and optional customData.wedding extension)
 * into a NikahContent object, falling back to rich demo content so the page
 * always renders complete and polished.
 */

import type { InvitationData } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
  safeFormatDateLong,
  safeFormatTime,
} from '@/src/infrastructure/lib/weddingDataHelper';

function img(id: string, w = 1400): string {
  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;
}

const STOCK = {
  hero: img('1543168259-7b1ee3a2d34d', 2000),
  mosque: img('1565551064841-9a9f4b7a3b3f', 900),
  couple: img('1606216794074-735e91aa2c92', 900),
  meal: img('1555939594-58d7cb561ad1', 900),
  venue: img('1519682337058-a94d519337bc', 900),
};

export interface Event {
  title: string;
  date: string;
  time: string;
  venue: string;
  description: string;
  image?: string;
}

export interface NikahContent {
  bismillah: string;
  hero: {
    kicker: string;
    title: string;
    subtitle: string;
    host: string;
    dateLabel: string;
    venueShort: string;
    background: string;
  };
  families: {
    label: string;
    title: string;
    groomSide: { title: string; members: { name: string; relation: string }[] };
    brideSide: { title: string; members: { name: string; relation: string }[] };
  };
  nikah: {
    label: string;
    title: string;
    description: string;
    image: string;
    date: string;
    time: string;
    venue: string;
    mehr: string;
  };
  walima: {
    label: string;
    title: string;
    events: Event[];
  };
  rsvp: {
    label: string;
    title: string;
    description: string;
    deadline: string;
    externalLink?: string;
  };
  footer: {
    message: string;
    signature: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────

const hasText = (v?: string | null): v is string =>
  typeof v === 'string' && v.trim().length > 0;

function formatLong(iso?: string): string {
  if (!hasText(iso)) return 'Date to be announced';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function formatTime(iso?: string, fallback = ''): string {
  if (!hasText(iso)) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// ── Demo Content ───────────────────────────────────────────────────────────

const DEMO: NikahContent = {
  bismillah: 'بسم الله الرحمن الرحيم',
  hero: {
    kicker: 'With the blessings of Allah',
    title: 'Ahmed & Ayesha',
    subtitle: 'are getting married',
    host: 'The Khan & Siddiqui Families',
    dateLabel: 'Sunday, 7 December 2025',
    venueShort: 'The Emerald Banquet, Mumbai',
    background: STOCK.hero,
  },
  families: {
    label: 'The Families',
    title: 'Joined in Nikah by the grace of Allah',
    groomSide: {
      title: 'Khan Family',
      members: [
        { name: 'Mr. Yusuf Khan', relation: 'Father of the Groom' },
        { name: 'Mrs. Fatima Khan', relation: 'Mother of the Groom' },
      ],
    },
    brideSide: {
      title: 'Siddiqui Family',
      members: [
        { name: 'Mr. Imran Siddiqui', relation: 'Father of the Bride' },
        { name: 'Mrs. Zainab Siddiqui', relation: 'Mother of the Bride' },
      ],
    },
  },
  nikah: {
    label: 'The Nikah',
    title: 'The Sacred Union',
    description:
      'In the presence of family, friends and the Qazi, Ahmed and Ayesha will be joined in Nikah — a bond of love, mercy and tranquillity blessed by Allah.',
    image: STOCK.mosque,
    date: 'Sunday, 7 December 2025',
    time: 'After Zuhr · 2:00 PM',
    venue: 'The Emerald Banquet, Mumbai',
    mehr: 'A token of love and commitment, agreed with the blessings of both families.',
  },
  walima: {
    label: 'Walima Reception',
    title: 'A feast of gratitude',
    events: [
      {
        title: 'Nikah Ceremony',
        date: 'Sunday, 7 December 2025',
        time: '2:00 PM',
        venue: 'The Emerald Banquet, Andheri',
        description: 'The marriage contract and blessings',
        image: STOCK.couple,
      },
      {
        title: 'Walima Feast',
        date: 'Sunday, 7 December 2025',
        time: '7:30 PM onwards',
        venue: 'Grand Hall, The Emerald Banquet',
        description: 'A celebratory meal hosted by the groom’s family',
        image: STOCK.meal,
      },
    ],
  },
  rsvp: {
    label: 'Join Us',
    title: 'Kindly Respond',
    description:
      'Your presence at this blessed occasion would be a joy. Please respond by the date below.',
    deadline: '20 November 2025',
  },
  footer: {
    message: 'JazakAllahu Khairan — with gratitude and prayers',
    signature: 'Ahmed & Ayesha',
  },
};

// ── Resolver ──────────────────────────────────────────────────────────────

export function resolveContent(data: InvitationData): NikahContent {
  const wedding = data.customData?.wedding ?? {};
  const extracted = extractCoupleNames(data.eventName);

  const groomName = wedding.groom?.fullName || wedding.groom?.nickname || extracted?.groom || 'Groom';
  const brideName = wedding.bride?.fullName || wedding.bride?.nickname || extracted?.bride || 'Bride';
  const coupleNames = (extracted?.groom && extracted?.bride)
    ? `${extracted.groom} & ${extracted.bride}`
    : ((wedding.groom?.fullName || wedding.groom?.nickname) && (wedding.bride?.fullName || wedding.bride?.nickname))
      ? `${wedding.groom.fullName || wedding.groom.nickname} & ${wedding.bride.fullName || wedding.bride.nickname}`
      : (hasText(data.eventName) ? data.eventName.trim() : `${groomName} & ${brideName}`);

  const groom = wedding.groom ?? {};
  const bride = wedding.bride ?? {};

  const groomFather = groom.fatherName;
  const groomMother = groom.motherName;
  const groomFamily = groom.family;

  const brideFather = bride.fatherName;
  const brideMother = bride.motherName;
  const brideFamily = bride.family;

  const groomFamilyTitle = groomFamily ? `${groomFamily} Family` : (hasText(data.hostName) ? data.hostName : `${groomName}'s Family`);
  const brideFamilyTitle = brideFamily ? `${brideFamily} Family` : `${brideName}'s Family`;

  const groomSideMembers = [];
  if (groom.parents) {
    groomSideMembers.push({ name: groom.parents, relation: 'Parents of the Groom' });
  } else {
    if (groomFather) groomSideMembers.push({ name: groomFather, relation: 'Father of the Groom' });
    if (groomMother) groomSideMembers.push({ name: groomMother, relation: 'Mother of the Groom' });
  }

  const brideSideMembers = [];
  if (bride.parents) {
    brideSideMembers.push({ name: bride.parents, relation: 'Parents of the Bride' });
  } else {
    if (brideFather) brideSideMembers.push({ name: brideFather, relation: 'Father of the Bride' });
    if (brideMother) brideSideMembers.push({ name: brideMother, relation: 'Mother of the Bride' });
  }

  const defaultGroomSide = hasText(data.hostName)
    ? { title: data.hostName, members: [{ name: data.hostName, relation: 'Host & Family' }] }
    : DEMO.families.groomSide;

  const defaultBrideSide = hasText(data.hostName)
    ? { title: `${brideName}'s Family`, members: [{ name: `${brideName}'s Family`, relation: 'Family of the Bride' }] }
    : DEMO.families.brideSide;

  const resolvedFamilies = {
    ...DEMO.families,
    groomSide: groomSideMembers.length > 0
      ? { title: groomFamilyTitle, members: groomSideMembers }
      : defaultGroomSide,
    brideSide: brideSideMembers.length > 0
      ? { title: brideFamilyTitle, members: brideSideMembers }
      : defaultBrideSide,
  };

  const weddingEvents = wedding.weddingEvents ?? [];
  const nikahEvent = weddingEvents.find((e: any) => /nikah/i.test(e.title || ''));

  const effectiveDate = parseFlexibleDate(data.eventDate) || new Date(Date.now() + 30 * 86400000);
  const venueShort = hasText(data.eventVenue) ? data.eventVenue : 'Venue to be announced';

  const resolvedNikahDate = hasText(wedding.nikahDate)
    ? safeFormatDateLong(wedding.nikahDate)
    : (nikahEvent
      ? safeFormatDateLong(nikahEvent.date)
      : (hasText(data.eventDate) ? safeFormatDateLong(data.eventDate) : 'Date to be announced'));

  const resolvedNikahTime = hasText(wedding.nikahTime)
    ? wedding.nikahTime
    : (nikahEvent
      ? [safeFormatTime(nikahEvent.startTime), safeFormatTime(nikahEvent.endTime)].filter(Boolean).join(' – ')
      : (hasText(data.eventTime) ? data.eventTime : '2:00 PM'));

  const resolvedNikahVenue = hasText(wedding.nikahVenue)
    ? wedding.nikahVenue
    : (nikahEvent
      ? (nikahEvent.venue || venueShort)
      : venueShort);

  const resolvedNikah = {
    ...DEMO.nikah,
    title: hasText(wedding.nikahQazi) ? `Sacred Nikah solemnized by ${wedding.nikahQazi}` : DEMO.nikah.title,
    description: hasText(wedding.nikahWelcomeMessage)
      ? wedding.nikahWelcomeMessage
      : `In the presence of family, friends and the Qazi, ${groomName} and ${brideName} will be joined in Nikah — a bond of love, mercy and tranquillity blessed by Allah.`,
    date: resolvedNikahDate,
    time: resolvedNikahTime,
    venue: resolvedNikahVenue,
    mehr: hasText(wedding.nikahMehr) ? wedding.nikahMehr : DEMO.nikah.mehr,
  };

  const scheduleEvents: Event[] | undefined = wedding.weddingEvents?.length
    ? wedding.weddingEvents.map((e: any) => ({
        title: e.title,
        date: safeFormatDateLong(e.date),
        time: [safeFormatTime(e.startTime), safeFormatTime(e.endTime)].filter(Boolean).join(' – '),
        venue: e.venue || venueShort,
        description: e.description || '',
        image: e.coverImageUrl ?? STOCK.venue,
      }))
    : [
        {
          title: 'Nikah Ceremony',
          date: resolvedNikahDate,
          time: resolvedNikahTime,
          venue: resolvedNikahVenue,
          description: 'The marriage contract and blessings',
          image: STOCK.couple,
        },
        {
          title: 'Walima Feast',
          date: resolvedNikahDate,
          time: '7:30 PM onwards',
          venue: resolvedNikahVenue,
          description: 'A celebratory meal with family and loved ones',
          image: STOCK.meal,
        },
      ];

  return {
    bismillah: wedding.nikahBismillah ?? DEMO.bismillah,
    hero: {
      kicker: DEMO.hero.kicker,
      title: coupleNames,
      subtitle: DEMO.hero.subtitle,
      host: hasText(data.hostName) ? data.hostName : (hasText(data.eventName) ? coupleNames : DEMO.hero.host),
      dateLabel: resolvedNikahDate,
      venueShort: resolvedNikahVenue,
      background: data.mediaUrls?.[0] ?? DEMO.hero.background,
    },
    families: resolvedFamilies,
    nikah: resolvedNikah,
    walima: {
      ...DEMO.walima,
      events: scheduleEvents,
    },
    rsvp: {
      ...DEMO.rsvp,
      description: hasText(data.customMessage) ? data.customMessage : DEMO.rsvp.description,
      externalLink: hasText(data.rsvpLink) ? data.rsvpLink : undefined,
    },
    footer: {
      message: DEMO.footer.message,
      signature: coupleNames,
    },
  };
}
